import { isClonedVoiceCode } from '@vbee-holding/aivoice-shared-lib';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Checkbox, IconButton, Typography } from '@mui/material';
import {
  WarningRounded as WarningRoundedIcon,
  CallMerge as MergeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { OrderedSet } from 'immutable';
import {
  CONVERTING_STATUS,
  MAX_CHARACTER_PER_BLOCK_SRT,
  MIN_GAP_BETWEEN_BLOCKS,
  BLOCK_MARGIN,
  BLOCK_STATUS,
  TRANSLATION_STATUS,
  REWRITE_STATUS,
} from '@src/constants/project';
import { HISTORY_SCOPE, HISTORY_LABEL } from '@src/constants/history';
import {
  ContentState,
  Editor,
  EditorState,
  Modifier,
  getDefaultKeyBinding,
} from 'draft-js';
import sanitize from 'sanitize-html';
import Tooltip from '@src/components/Tooltip';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { COLOR } from '@src/styles/color';
import addBlock from '@src/assets/icons/add-block.svg';
import {
  BREAK_TIME_REGEX,
  DEFAULT_BREAK_TIME,
  DEFAULT_SPEED,
  VOICE_OWNERSHIP,
} from '@src/constants/voice';
import { v4 as uuidv4 } from 'uuid';
import { countTextLength } from '@src/services/tts';
import BreakTimeHighlight from '@src/containers/TTS/BreakTimeHightLight';
import BlackWordHighLight from '@src/containers/TTS/BlackWordHighLight';
import {
  generateDecorator,
  generateCompositeDecorator,
} from '@src/services/editor';
import { checkBrowser } from '@src/utils/browser';
import { MAX_CHARACTER_LENGTH, VALID_LETTERS } from '@src/constants/tts';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  downloadFileByUrl,
  // downloadFileUsingUrl,
  getFileName,
} from '@src/utils/file';
import { removeVietnameseTones } from '@src/utils/string';
import {
  convertSubtitleTimeToSeconds,
  convertSecondsToSubtitleTime,
} from '@src/utils/time';
import { API_URL, LANDING_PAGE_URL } from '@src/configs';
import { CATEGORY_TYPE, PLATFORM_TYPE } from '@src/constants/support';
import { VOICE_STATUS } from '@src/constants/voiceCloning';
import useRecommendedCharWarning from '@src/hooks/useRecommendedCharWarning';
import { getPresignedAudioUrl } from '@src/apis/requests';
import { trackBlockAudioDownloaded } from '@src/services/moengage';
import {
  getBlockStatus,
  resolveBlockVoice,
  isBlockChanged,
  buildInsertedBlockTiming,
  validateBlockTimeChange,
  formatInvalidTexts,
  checkBlockTextError,
  sanitizeTitleFile,
  isBlockRewriting,
} from '@src/services/project';
import CommonButton from '@src/new-components/Button';
import NewTypography from '@src/new-components/Typography';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import VoiceSelectionDialog from '@src/containers/VoiceSelectionDialog';
import LimitedFeature from '@src/components/LimitedFeature';
import { convertFormatDate } from '@src/utils/date';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import { deleteBlockRewrite, rewriteProject } from '@src/apis/project';
import { RESOURCE } from '@src/constants';
import RecommendedCharWarning from './RecommendedCharWarning';
import TimeFrameInput from './TimeFrameInput';
import { StyledTimeFrameRow } from './TimeFrameInput.style';
import {
  StyledAddNewBlockZone,
  StyledBlockEditor,
  StyledContentBlock,
  StyledContentBlockWrapper,
  StyledInfoBlock,
  StyledOrdinalNumberBlock,
} from './index.style';
import MoreOptionBlock from '../../component/MoreOptionBlock';
import SynthesisButton from '../../component/SynthesisButton';
import NewSynthesisButton from '../../component/NewSynthesisButton';
import ConfigOptionBlock from '../../component/ConfigOptionBlock';
import CreditsCount from '../../component/CreditsCount';
import SpeakerSelectionPopover from '../SpeakerSelectionPopover';
import AddSpeaker from '../AddSpeaker';
import Rewrite from './Rewrite';

const ContentBlock = ({
  saveProject,
  onOpenPopupNotEnoughCredits,
  blockId,
  block,
  characterExceed,
  onAddNewParagraphs,
  isLastBlock,
  isFocusing,
  onFocusingBlock,
  onSaveProject,
  voiceDefault,
  isPlaying,
  hasVideoTimeline = false,
  isPlayingBlock,
  isDownloading,
  onOpenOutOfCreditsDialog,
  onOpenWrongSyntaxDialog,
  onOpenWrongSyntaxDialogV2,
  onOpenDiscardedVoiceCloningDialog,
  checkValidCreateSynthesis,
  isHighlight,
  handlePlayAudio,
  projectId,
  originalBlock,
  onConvertBlockToAudio,
  enableDubbingBlockAudio,
  canDownloadBlockAudio,
  currentPlayingBlockId,
  onSeekInBlock,
  blockAudioCurrentTime,
  blockAudioDuration,
  audioType,
  setDeleteDialogData,
  onMergeBlock,
  nextBlock,
  prevBlock,
  speakers = [],
  areBlocksConsecutive = true,
  foundBlackWords = [],
  isBlockUIImprovement = false,
  enableCreditUsagePreview,
  isBlockSelectionLocked = false,
}) => {
  const { t, i18n } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { language } = i18n;
  const inputRef = useRef(null);
  const userId = useSelector((state) => state.auth.user?.id);
  const userEmail = useSelector((state) => state.auth.user?.email);
  const userPhoneNumber = useSelector((state) => state.auth.user?.phoneNumber);
  const userPackageCode = useSelector((state) => state.auth.user?.packageCode);
  const user = useMemo(
    () => ({
      id: userId,
      email: userEmail,
      phoneNumber: userPhoneNumber,
      packageCode: userPackageCode,
    }),
    [userId, userEmail, userPhoneNumber, userPackageCode],
  );
  // Narrow selectors, one field each. Subscribing to the whole `state.project`
  // slice re-rendered every mounted block on any project action — the reducer
  // returns a new slice object every time (see UPDATE_BLOCK), so the reference
  // always changed and this component's memo was bypassed. Profiling showed it
  // as ~6 ContentBlock re-renders per commit whose reason was `hooks`, not
  // `props`. These four fields all keep their identity across a block edit.
  const isProjectSynthesizing = useSelector(
    (state) => state.project.isProjectSynthesizing,
  );
  const translation = useSelector((state) => state.project.translation);
  const showOriginalText = useSelector(
    (state) => state.project.showOriginalText,
  );
  const blockIds = useSelector((state) => state.project.multiBlock?.blockIds);
  const { filterVoice } = useSelector((state) => state.voice);

  const indexBlock = useMemo(() => {
    const idx = blockIds.indexOf(blockId) + 1;
    return idx;
  }, [blockIds, blockId]);

  const dispatch = useDispatch();

  const {
    voiceCode = voiceDefault?.code,
    text = '',
    speed = DEFAULT_SPEED,
    breakTime = DEFAULT_BREAK_TIME,
    audioLink,
    status,
    requestId,
    showSuccessIcon,
    audioExpiredAt,
    isBlockEditting,
    speakerId,
    translatedText = '',
  } = block;
  // Seperately destructuring hasEmphasis and emphasis since hasEmphasis is dependent on voice?.hasEmphasis
  const maxCharLimit = MAX_CHARACTER_PER_BLOCK_SRT;
  const speakerConfigEnabled = getFeatureValue(FEATURE_KEYS.SPEAKER_CONFIG, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });
  const speaker = speakerConfigEnabled
    ? speakers?.find((s) => s.id === speakerId)
    : null;

  const voice = speaker?.voice;
  const aiTranslateEnabled = getFeatureValue(FEATURE_KEYS.AI_TRANSLATE, {
    userId: user?.id,
    email: user?.email,
  });
  const hasTranslation =
    aiTranslateEnabled && translation?.status === TRANSLATION_STATUS.SUCCESS;
  const blockStatus = getBlockStatus({
    block,
    originalBlock,
    audioType,
    maxCharLimit,
    blackWordsList: foundBlackWords,
    voice: resolveBlockVoice({ block, speakers, voiceDefault }),
    // Match the editor: after a successful translation the status is judged on
    // translatedText, so the filter and its counter agree with the error icon.
    isTranslationSuccess: hasTranslation,
  });

  const recommendedCharWarningEnabled = getFeatureValue(
    FEATURE_KEYS.SRT_RECOMMENDED_CHARACTER_WARNING,
    {
      userId: user?.id,
      email: user?.email,
    },
  );

  const rewriteEnabled = getFeatureValue(FEATURE_KEYS.AI_REWRITE, {
    userId: user?.id,
    email: user?.email,
  });

  // Always show translation in editor if available
  const editableText = (hasTranslation ? translatedText : text) || '';

  const { hasEmphasis = voice?.hasEmphasis, emphasis = 0 } = block;
  const isPublicVoiceCloning = voice && voice?.status === VOICE_STATUS.PUBLIC;
  const isVoiceCloningAboutToRemoved =
    voice && isPublicVoiceCloning && voice?.discardAt
      ? moment().isBefore(voice?.discardAt)
      : false;

  const voiceInActive = voice && !voice?.active;
  const isInValidPublicVoiceCloning =
    (voice &&
      voice?.userId !== user?.id &&
      ((voice?.discardAt && moment().isAfter(voice?.discardAt)) ||
        voice?.status === VOICE_STATUS.PRIVATE)) ||
    voiceInActive;

  const speakerName = speaker?.name || '';
  const {
    exceedMaxChar: exceedBlockMaxChar,
    invalidCharacterLengthTexts,
    hasInvalidCharacterLength,
    hasInvalidCharacters,
    hasError: hasBlockError,
    hasBlackWords,
  } = checkBlockTextError(editableText, maxCharLimit, foundBlackWords, voice);

  const {
    isExceeded: isExceededRecommendedChars,
    maxRecommended,
    currentChars,
    duration: blockDuration,
  } = useRecommendedCharWarning({
    text: editableText,
    startTime: block?.startTime,
    endTime: block?.endTime,
    voice,
    speed,
    hasError: hasBlockError,
    isFeatureEnabled: recommendedCharWarningEnabled,
  });

  const getBlockErrorConfig = () => {
    const sanitizeOptions = {
      allowedTags: ['br', 'p', 'strong'],
      allowedAttributes: {
        p: ['style'],
        br: ['style'],
      },
    };

    if (exceedBlockMaxChar) {
      return {
        message: t('exceedBlockMaxChar', {
          maxCredits: MAX_CHARACTER_PER_BLOCK_SRT.toLocaleString(language),
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacterLength) {
      return {
        message: t('invalidCharLengthMessage', {
          invalidTexts: formatInvalidTexts(invalidCharacterLengthTexts),
          maxCharacterLength: MAX_CHARACTER_LENGTH - 1,
        }),
        sanitizeOptions,
      };
    }
    if (hasInvalidCharacters) {
      return {
        message: t('invalidCharacter', { invalidText: editableText.trim() }),
        sanitizeOptions,
      };
    }
    if (hasBlackWords) {
      return {
        message: t('sentenceHasBlackWordsDescription'),
        sanitizeOptions,
      };
    }
    return { message: '', sanitizeOptions };
  };

  // Also treat this block's own IN_PROGRESS as synthesizing (mirrors TTS): the
  // per-block create-audio flow doesn't set the project-wide flag, so without
  // this the block's config controls stay enabled while its audio is converting.
  const isSynthesizing =
    isDownloading ||
    isProjectSynthesizing ||
    status === CONVERTING_STATUS.IN_PROGRESS;
  const isNextBlockSynthesizing =
    nextBlock?.status === CONVERTING_STATUS.IN_PROGRESS;
  const hasSuccessSynthesized =
    audioLink && status === CONVERTING_STATUS.SUCCESS;
  const isAudioExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
  const showAudioPopover =
    status === CONVERTING_STATUS.SUCCESS ||
    status === CONVERTING_STATUS.FAILURE;
  const isThisBlockActive = currentPlayingBlockId === blockId;
  const popoverCurrentTime = isThisBlockActive ? blockAudioCurrentTime : 0;
  const popoverDuration = isThisBlockActive ? blockAudioDuration : 0;
  const cannotMerge =
    !nextBlock ||
    isNextBlockSynthesizing ||
    isSynthesizing ||
    !areBlocksConsecutive;

  const [content, setContent] = useState(
    EditorState.createEmpty(
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    ),
  );
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [openAddSpeaker, setOpenAddSpeaker] = useState(false);
  const [openVoices, setOpenVoices] = useState(false);
  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);

  const editBlockTimeDubbingEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_EDIT_TIME_BLOCK,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email },
  );

  // Manual override for user-typed invalid input (before it reaches redux).
  // null = no override, fall through to derived validation below.
  const [manualStartTimeError, setManualStartTimeError] = useState(null);
  const [manualEndTimeError, setManualEndTimeError] = useState(null);

  // Derived validation from current block values — covers initial load and
  // backend-hydrated timestamps (transcript/translate) so bad timestamps
  // surface immediately in the UI instead of only at synthesize time.
  const validateTime = (type, value) => {
    if (!value) return { hasError: false, message: '' };
    try {
      const { valid, errorKey } = validateBlockTimeChange({
        type,
        newTime: value,
        currentBlock: block,
        prevBlock,
        nextBlock,
      });
      return valid
        ? { hasError: false, message: '' }
        : { hasError: true, message: errorKey };
    } catch {
      return { hasError: true, message: 'invalidTimeFormat' };
    }
  };

  const derivedStartTimeError = useMemo(
    () => validateTime('start', block?.startTime),
    [
      block?.startTime,
      block?.endTime,
      prevBlock?.endTime,
      nextBlock?.startTime,
    ],
  );
  const derivedEndTimeError = useMemo(
    () => validateTime('end', block?.endTime),
    [
      block?.startTime,
      block?.endTime,
      prevBlock?.endTime,
      nextBlock?.startTime,
    ],
  );

  const startTimeError = manualStartTimeError || derivedStartTimeError;
  const endTimeError = manualEndTimeError || derivedEndTimeError;

  // Handler for changing start time
  const handleChangeStartTime = (newTime) => {
    try {
      const validation = validateBlockTimeChange({
        type: 'start',
        newTime,
        currentBlock: block,
        prevBlock,
        nextBlock,
      });

      if (!validation.valid) {
        setManualStartTimeError((prev) =>
          prev?.hasError && prev.message === validation.errorKey
            ? prev
            : { hasError: true, message: validation.errorKey },
        );
        return;
      }

      setManualStartTimeError(null);
      const timingOptions = undoRedoEnabled
        ? {
            history: {
              commit: true,
              scope: HISTORY_SCOPE.DUBBING,
              label: HISTORY_LABEL.CHANGE_BLOCK_TIMING,
            },
          }
        : {};
      dispatch(
        actions.project.updateBlock(
          blockId,
          { startTime: newTime, blockStatus },
          timingOptions,
        ),
      );
    } catch (err) {
      setManualStartTimeError((prev) =>
        prev?.hasError && prev.message === 'invalidTimeFormat'
          ? prev
          : { hasError: true, message: 'invalidTimeFormat' },
      );
    }
  };

  // Handler for changing end time
  const handleChangeEndTime = (newTime) => {
    try {
      const validation = validateBlockTimeChange({
        type: 'end',
        newTime,
        currentBlock: block,
        prevBlock,
        nextBlock,
      });

      if (!validation.valid) {
        setManualEndTimeError((prev) =>
          prev?.hasError && prev.message === validation.errorKey
            ? prev
            : { hasError: true, message: validation.errorKey },
        );
        return;
      }

      setManualEndTimeError(null);
      const timingOptions = undoRedoEnabled
        ? {
            history: {
              commit: true,
              scope: HISTORY_SCOPE.DUBBING,
              label: HISTORY_LABEL.CHANGE_BLOCK_TIMING,
            },
          }
        : {};
      dispatch(
        actions.project.updateBlock(
          blockId,
          { endTime: newTime, blockStatus },
          timingOptions,
        ),
      );
    } catch (err) {
      setManualEndTimeError((prev) =>
        prev?.hasError && prev.message === 'invalidTimeFormat'
          ? prev
          : { hasError: true, message: 'invalidTimeFormat' },
      );
    }
  };

  const handleOpenVoices = (event) => {
    if (speakerConfigEnabled) {
      // When speaker config is enabled, open speaker selection popover
      setPopoverAnchorEl(event?.currentTarget);
    } else {
      // When speaker config is disabled, open voice dialog directly (like TTS)
      const voiceOwnershipFilter = filterVoice?.voiceOwnership;
      const isClonedVoice = isClonedVoiceCode(voice?.code);
      let voiceOwnership = isClonedVoice
        ? VOICE_OWNERSHIP.PERSONAL
        : VOICE_OWNERSHIP.VBEE;

      if (
        isClonedVoice &&
        (voiceOwnershipFilter === VOICE_OWNERSHIP.COMMUNITY ||
          voice?.userId !== userId)
      )
        voiceOwnership = VOICE_OWNERSHIP.COMMUNITY;

      if (!voiceOwnershipFilter || voiceOwnershipFilter !== voiceOwnership)
        dispatch(
          actions.voice.changeFilterVoice({
            ...filterVoice,
            voiceOwnership,
          }),
        );

      setOpenVoices(true);
    }
  };
  const handleCloseVoices = () => setOpenVoices(false);
  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };
  const handleOpenAddSpeaker = () => {
    setPopoverAnchorEl(null);
    setOpenAddSpeaker(true);
  };
  const handleCloseAddSpeaker = () => {
    setOpenAddSpeaker(false);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogData(blockId);
  };

  const getSelectionOffset = (editorState) => {
    // Get the current content and selection states
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    // Get the key of the block where the selection starts
    const startKey = selectionState.getStartKey();

    // Get the offset within the starting block
    const startOffset = selectionState.getStartOffset();

    // Get a list of all content blocks
    const blockMap = contentState.getBlockMap();
    const blocks = blockMap.toList();

    let characterOffset = 0;

    // Find the index of the start block in the list
    const startBlockIndex = blocks.findIndex(
      (blockItem) => blockItem.getKey() === startKey,
    );

    // Iterate through all blocks before the start block and add their length + 1 (for the newline)
    for (let i = 0; i < startBlockIndex; i += 1) {
      characterOffset += blocks.get(i).getLength() + 1;
    }

    // Add the offset within the start block
    characterOffset += startOffset;

    return characterOffset;
  };

  // Check if cursor is in valid position for splitting and get disable reason
  const { canSplitBlock, splitBlockDisableReason } = useMemo(() => {
    if (!editableText || isSynthesizing) {
      return { canSplitBlock: false, splitBlockDisableReason: null };
    }

    const disableSplitWhenRewrite = [
      REWRITE_STATUS.PENDING,
      REWRITE_STATUS.SUCCESS,
    ].includes(block?.rewrite?.status);

    if (disableSplitWhenRewrite) {
      return { canSplitBlock: false, splitBlockDisableReason: null };
    }

    const offset = getSelectionOffset(content);

    // For dubbing projects, check if block duration is at least 0.002s
    if (block?.startTime && block?.endTime) {
      const originalStartTime = convertSubtitleTimeToSeconds(block.startTime);
      const originalEndTime = convertSubtitleTimeToSeconds(block.endTime);
      const duration = originalEndTime - originalStartTime;

      // Disable split if duration is less than 0.002s
      if (duration < MIN_GAP_BETWEEN_BLOCKS) {
        return {
          canSplitBlock: false,
          splitBlockDisableReason: 'splitBlockTooltipDurationTooShort',
        };
      }
    }
    // Must have text before and after cursor
    if (offset <= 0 || offset >= editableText.length) {
      return { canSplitBlock: false, splitBlockDisableReason: null };
    }

    return { canSplitBlock: true, splitBlockDisableReason: null };
  }, [content, editableText, isSynthesizing, isLastBlock, block]);

  const isCheckBlockChanged = useMemo(() => {
    const withoutExtension = block?.audioLink?.split('.')?.pop();
    const currentBlock = {
      text: block?.text,
      // When a translation is active the block is synthesized from
      // translatedText (see handleChangeText / handleConvertBlockToAudioDubbing),
      // so an edit to it must count as a change — otherwise the Play button stays
      // shown and the Convert button never re-enables.
      translatedText: block?.translatedText,
      voiceCode: block?.voiceCode,
      speed: block?.speed,
      audioType: withoutExtension,
      emphasis: block?.emphasis,
    };
    const blockOriginal = {
      text: originalBlock?.text,
      translatedText: originalBlock?.translatedText,
      voiceCode: originalBlock?.voiceCode,
      speed: originalBlock?.speed,
      audioType,
      emphasis: originalBlock?.emphasis,
    };

    return isBlockChanged(currentBlock, blockOriginal);
  }, [block, originalBlock, audioType]);

  const isDisableConvertBtn = useMemo(
    () =>
      !editableText ||
      countTextLength(editableText) > MAX_CHARACTER_PER_BLOCK_SRT ||
      hasInvalidCharacterLength ||
      hasInvalidCharacters ||
      hasBlackWords ||
      // A rewrite in flight is about to replace this text — synthesizing it now
      // spends credits on audio that lands already stale.
      isBlockRewriting(block),
    [
      editableText,
      hasInvalidCharacterLength,
      hasInvalidCharacters,
      hasBlackWords,
      // Only the rewrite status, not `block`: the whole block changes on every
      // keystroke, and this would be recomputed for each one.
      block?.rewrite?.status,
    ],
  );

  const isDisablePlayAudio = useMemo(
    () => isCheckBlockChanged || !hasSuccessSynthesized || isAudioExpired,
    [isCheckBlockChanged, hasSuccessSynthesized, isAudioExpired],
  );

  // Check if can insert block between current and next block (for dubbing projects)
  const canInsertBlockBetween = useMemo(() => {
    if (isLastBlock || !nextBlock) return true;

    const currentEndTime = block?.endTime
      ? convertSubtitleTimeToSeconds(block.endTime)
      : 0;
    const nextStartTime = nextBlock?.startTime
      ? convertSubtitleTimeToSeconds(nextBlock.startTime)
      : 0;
    const gap = nextStartTime - currentEndTime;
    return gap >= MIN_GAP_BETWEEN_BLOCKS;
  }, [isLastBlock, nextBlock, block]);

  const isLocalEdit = useRef(false);
  const handleChangeContent = (newContent) => setContent(newContent);

  // Handle split block action
  const handleSplitBlock = () => {
    if (!canSplitBlock) return;

    if (undoRedoEnabled) {
      dispatch(
        actions.history.beginTransaction(
          HISTORY_SCOPE.DUBBING,
          HISTORY_LABEL.SPLIT_BLOCK,
        ),
      );
    }

    const contentState = content.getCurrentContent();
    const plainText = contentState.getPlainText();
    const offset = getSelectionOffset(content);
    // Split text at cursor position
    const textBefore = plainText.substring(0, offset);
    const textAfter = plainText.substring(offset);
    // Check if cursor is at the start of a block

    // Calculate timing for dubbing projects based on character ratio
    const textField = hasTranslation ? 'translatedText' : 'text';
    const firstBlockUpdate = { [textField]: textBefore?.trim() };
    const newBlockData = {
      voice,
      voiceCode,
      speed,
      breakTime,
      [textField]: textAfter?.trim(),
      speakerId,
      blockStatus: BLOCK_STATUS.DRAFT,
      emphasis,
      hasEmphasis,
    };

    // If block has startTime and endTime (dubbing project), calculate timing
    if (block?.startTime && block?.endTime) {
      const originalStartTime = convertSubtitleTimeToSeconds(block.startTime);
      const originalEndTime = convertSubtitleTimeToSeconds(block.endTime);
      const originalDuration = originalEndTime - originalStartTime;
      const originalTextLength = plainText.length;

      // Calculate character ratio
      const textBeforeLength = textBefore.trim().length;
      const ratio = textBeforeLength / originalTextLength;

      // Calculate end time of first block based on character ratio
      // End time = start time + (duration * ratio)
      let firstBlockEndTimeSeconds =
        originalStartTime + originalDuration * ratio;

      // Round up to 0.001 (Math.ceil with 0.001 step)
      firstBlockEndTimeSeconds =
        Math.ceil(firstBlockEndTimeSeconds * 1000) / 1000;

      // Ensure it's <= original end time - 0.001
      const maxEndTime = originalEndTime - 0.001;
      if (firstBlockEndTimeSeconds > maxEndTime) {
        firstBlockEndTimeSeconds = maxEndTime;
      }

      // First block: startTime = original startTime, endTime = calculated
      firstBlockUpdate.startTime = block.startTime;
      firstBlockUpdate.endTime = convertSecondsToSubtitleTime(
        Number(firstBlockEndTimeSeconds.toFixed(3)),
      );

      // Second block: startTime = first block endTime, endTime = original endTime
      newBlockData.startTime = convertSecondsToSubtitleTime(
        Number(firstBlockEndTimeSeconds.toFixed(3)),
      );
      newBlockData.endTime = block.endTime;
    }

    // Update current block with text before cursor and calculated timing
    dispatch(actions.project.updateBlock(blockId, firstBlockUpdate));

    // Update editor state for current block
    const newContentState = ContentState.createFromText(textBefore?.trim());
    const newEditorState = EditorState.createWithContent(
      newContentState,
      generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
    );
    handleChangeContent(newEditorState);

    // Create new block with text after cursor
    const newBlockId = uuidv4();
    const newBlock = {
      ...newBlockData,
      id: newBlockId,
    };

    // Add new block after current block
    onAddNewParagraphs(blockId, [newBlock]);

    // Focus on new block after it's added
    setTimeout(() => {
      onFocusingBlock(newBlockId);
      if (undoRedoEnabled) {
        dispatch(actions.history.endTransaction(HISTORY_SCOPE.DUBBING));
      }
    }, 100);
  };

  // Handle adding new block via Ctrl+Enter (separate from split block feature)
  const handleAddNewBlockOnShortcut = () => {
    if (isSynthesizing) return;

    // Calculate timing for dubbing projects
    const timingData = buildInsertedBlockTiming({
      currentBlock: block,
      nextBlock,
      blockMargin: BLOCK_MARGIN,
    });

    // Create new empty block with same config as current block
    const newBlockId = uuidv4();
    const newBlock = {
      voice,
      voiceCode,
      speed,
      breakTime,
      id: newBlockId,
      text: '',
      speakerId,
      blockStatus: BLOCK_STATUS.DRAFT,
      emphasis,
      hasEmphasis,
      ...timingData,
    };

    // Add new block after current block
    onAddNewParagraphs(blockId, [newBlock]);

    // Focus on new block after it's added
    onFocusingBlock(newBlockId);
  };

  // Handle keyboard shortcuts for adding new block
  const keyBindingFn = (e) => {
    // Detect Ctrl+Enter (Windows/Linux) or Cmd+Enter (Mac)
    if (
      e.keyCode === 13 && // Enter key
      (e.ctrlKey || e.metaKey) // Ctrl or Cmd key
    ) {
      return 'add-new-block-command';
    }
    return getDefaultKeyBinding(e);
  };

  const handleKeyCommand = (command) => {
    if (command === 'add-new-block-command' && canInsertBlockBetween) {
      handleAddNewBlockOnShortcut();
      return 'handled';
    }
    if (undoRedoEnabled && (command === 'undo' || command === 'redo')) {
      return 'handled';
    }
    return 'not-handled';
  };

  const handleChangeVoice = (newVoice) => {
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.CHANGE_BLOCK_VOICE,
          },
        }
      : {};
    dispatch(
      actions.project.updateBlock(
        blockId,
        {
          hasEmphasis: newVoice?.hasEmphasis,
          emphasis: 0,
          voiceCode: newVoice?.code,
          blockStatus,
          voice: {
            code: newVoice?.code,
            name: newVoice?.name,
            roundImage: newVoice?.roundImage,
            language: {
              roundImage: newVoice?.language?.roundImage,
            },
            level: newVoice?.level,
            provider: newVoice?.provider,
            eolDate: newVoice?.eolDate,
            creditFactor: newVoice?.creditFactor,
            canonicalVoice: newVoice?.canonicalVoice,
            discardAt: newVoice?.discardAt,
            retentionDays: newVoice?.retentionDays,
            status: newVoice?.status,
            userId: newVoice?.userId,
            features: Array.isArray(newVoice?.features)
              ? newVoice.features
              : [],
            labels: newVoice?.labels,
          },
        },
        options,
      ),
    );
  };

  const handleChangeSpeaker = (newSpeaker) => {
    if (!speakerConfigEnabled) return;

    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.CHANGE_BLOCK_VOICE,
          },
        }
      : {};
    dispatch(
      actions.project.updateBlock(
        blockId,
        {
          hasEmphasis: newSpeaker?.hasEmphasis,
          emphasis: 0,
          voiceCode: newSpeaker?.voice?.code,
          speed: newSpeaker?.speed,
          speakerId: newSpeaker?.id,
          blockStatus,
          voice: {
            code: newSpeaker?.voice?.code,
            name: newSpeaker?.voice?.name,
            roundImage: newSpeaker?.voice?.roundImage,
            language: {
              roundImage: newSpeaker?.voice?.language?.roundImage,
            },
            level: newSpeaker?.voice?.level,
            provider: newSpeaker?.voice?.provider,
            eolDate: newSpeaker?.voice?.eolDate,
            creditFactor: newSpeaker?.voice?.creditFactor,
            canonicalVoice: newSpeaker?.voice?.canonicalVoice,
            discardAt: newSpeaker?.voice?.discardAt,
            retentionDays: newSpeaker?.voice?.retentionDays,
            status: newSpeaker?.voice?.status,
            userId: newSpeaker?.voice?.userId,
            active: newSpeaker?.voice?.active,
            features: Array.isArray(newSpeaker?.voice?.features)
              ? newSpeaker?.voice?.features
              : [],
            labels: newSpeaker?.voice?.labels,
          },
        },
        options,
      ),
    );
  };

  const handleChangeSpeed = (field, value) => {
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.CHANGE_BLOCK_SPEED,
          },
        }
      : {};
    dispatch(
      actions.project.updateBlock(
        blockId,
        { speed: value, blockStatus },
        options,
      ),
    );
  };

  const handleChangeEmphasis = (field, value) => {
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.CHANGE_BLOCK_EMPHASIS,
          },
        }
      : {};
    dispatch(
      actions.project.updateBlock(
        blockId,
        { emphasis: value, blockStatus },
        options,
      ),
    );
  };

  const handleChangeBreakTime = (value) => {
    const currentContent = content.getCurrentContent();
    const selection = content.getSelection();
    const label = `<break time=${value}s/>`;
    const textWithEntity = Modifier.insertText(
      currentContent,
      selection,
      label,
      OrderedSet.of('BOLD'),
    );

    dispatch(
      actions.project.updateBlock(blockId, {
        breakTime: value,
        text: textWithEntity.getPlainText(),
        blockStatus,
      }),
    );
    handleChangeContent(
      EditorState.push(content, textWithEntity, 'insert-characters'),
    );
  };

  const handleEditorBlur = () => {
    if (!undoRedoEnabled) return;
    dispatch(actions.history.flush(HISTORY_SCOPE.DUBBING));
  };

  const handleTimingBlur = () => {
    if (!undoRedoEnabled) return;
    dispatch(actions.history.flush(HISTORY_SCOPE.DUBBING));
  };

  const handleChangeText = (value) => {
    const field = hasTranslation ? 'translatedText' : 'text';
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.EDIT_BLOCK_TEXT,
          },
        }
      : {};
    dispatch(
      actions.project.updateBlock(
        blockId,
        { [field]: value, blockStatus },
        options,
      ),
    );
  };

  const handleEditorChange = (editorState) => {
    const selectionState = editorState.getSelection();
    if (!selectionState.toJS().hasFocus) return;
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText('\n');
    let newContent = editorState;

    const isContentChanged = !content
      .getCurrentContent()
      .equals(editorState.getCurrentContent());
    if (isContentChanged) {
      const currentStyle = editorState.getCurrentInlineStyle();
      if (currentStyle && currentStyle.size) {
        newContent = EditorState.setInlineStyleOverride(
          editorState,
          OrderedSet.of(),
        );
      }
      handleChangeContent(newContent);
      isLocalEdit.current = true;
      handleChangeText(plainText);
    } else {
      handleChangeContent(editorState);
    }
  };

  const handleAddNewParagraphs = () => {
    onAddNewParagraphs(blockId);
  };

  const handlePastedText = () => false;

  // While the video timeline is playing, focus belongs to the playhead: it moves
  // to whatever block the clock is inside (see syncMedia in
  // useVideoDubbingAudioPlayer). Taking focus by hand would seek playback to
  // this block — jumping away from what is being listened to — so the block
  // stops answering clicks and its editor goes read-only (a caret IS focus).
  //
  // Scoped to a video-dubbing project with the timeline on: every other project
  // (plain TTS, audio/SRT dubbing) has no playhead to hand focus to and keeps
  // click-to-focus exactly as before.
  const isFocusLockedByPlayback = hasVideoTimeline && isPlaying;

  const handleFocusOnBlock = () => {
    if (isFocusLockedByPlayback) return;
    onFocusingBlock(blockId);
    inputRef.current?.focus();
  };

  const handleDownloadBlockAudio = async () => {
    const response = await getPresignedAudioUrl(requestId);
    if (response?.status) {
      const audioUrl = response.result?.audio;
      const ext = getFileName(audioLink).split('.').pop();
      const audioName = sanitizeTitleFile(
        removeVietnameseTones(editableText),
      ).replace(/\s+/g, '_');
      const fileName = `${indexBlock}-${audioName}-${Date.now()}`;
      downloadFileByUrl(audioUrl, ext ? `${fileName}.${ext}` : fileName);
      trackBlockAudioDownloaded({ requestId });
    }
  };

  const handleOpenErrorReport = () => {
    const ticketMetadata = {
      requestId,
      voiceCode,
      userId,
      blockId,
      projectId,
    };
    const base64TicketMetadata = btoa(JSON.stringify(ticketMetadata));
    const encodedTicketMetadata = encodeURIComponent(base64TicketMetadata);

    window.open(
      `${LANDING_PAGE_URL}/support?metadata=${encodedTicketMetadata}&platform=${PLATFORM_TYPE.DESKTOP_WEB}&category=${CATEGORY_TYPE.PROJECT}`,
      '_blank',
    );
  };

  useEffect(() => {
    if (isLocalEdit.current) {
      isLocalEdit.current = false;
      return;
    }
    if (editableText) {
      const contentEditor = EditorState.createWithContent(
        ContentState.createFromText(editableText),
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(contentEditor);
    } else {
      // If editableText is empty, create empty editor state
      const emptyEditor = EditorState.createEmpty(
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(emptyEditor);
    }
  }, [editableText]);

  useEffect(() => {
    if (isFocusing) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isFocusing]);

  useEffect(() => {
    dispatch(actions.project.updateBlockStatus({ blockId, blockStatus }));
  }, [blockStatus]);

  useEffect(() => {
    const currentBrowser = checkBrowser();
    const decorators = [
      { regex: BREAK_TIME_REGEX, component: BreakTimeHighlight },
    ];
    if (foundBlackWords.length > 0) {
      const blackWordsRegex = foundBlackWords.map((bw) =>
        bw.replaceAll(' ', ' +'),
      );
      const blackWordPattern = !currentBrowser?.safariAgent
        ? new RegExp(
            `(?<![${VALID_LETTERS}])(${blackWordsRegex.join(
              '|',
            )})(?![${VALID_LETTERS}])`,
            'gi',
          )
        : new RegExp(
            `(^|[^${VALID_LETTERS}])(${blackWordsRegex.join(
              '|',
            )})([^${VALID_LETTERS}]|$)`,
            'gi',
          );
      decorators.push({
        regex: blackWordPattern,
        component: BlackWordHighLight,
      });
    }
    setContent((prev) =>
      EditorState.set(prev, {
        decorator: generateCompositeDecorator(decorators),
      }),
    );
  }, [foundBlackWords.join(','), block?.translatedText]);

  const handleRewrite = async () => {
    // One of the two per-block operations at a time: a rewrite is about to
    // replace this block's text, and a synthesis of that text is already paid
    // for. Whichever started first owns the block until it lands. The local
    // `loadingRewrite` flag this used to keep was invisible to every other
    // guard, which is what let the two run at once.
    if (isSynthesizing || isBlockRewriting(block)) return;

    const webhookUrl = `${API_URL}/api/v1${RESOURCE.PROJECTS}/${projectId}${RESOURCE.REWRITE}/webhook`;
    const requestBody = {
      projectId,
      webhookUrl,
      blocks: [
        {
          maxCharacters: maxRecommended,
          blockId,
          originalText: editableText,
        },
      ],
    };

    // PENDING goes in BEFORE the await, so every guard sees the rewrite for the
    // whole window — including the save, which can take as long as the call.
    // The `rewrite` draft field isn't content, so skip auto-save: kicking off a
    // rewrite must not flip the save status to "Saving".
    // Keep blockStatus: a rewrite draft is not a content change, so it must not
    // let the reducer reset the status to DRAFT (the filter counter reads it).
    const setRewrite = (rewrite) =>
      dispatch(
        actions.project.updateBlock(
          blockId,
          { rewrite, blockStatus },
          { skipAutoSave: true },
        ),
      );

    // `optimistic` says "guarded here, not on the server yet". The progress poll
    // in Content/index.jsx keys off this same PENDING; without the marker it
    // fires now — before the POST below — reads the PREVIOUS job and overwrites
    // this PENDING with that stale status, dropping the guard mid-flight. Blocks
    // that arrive PENDING from the server carry no marker, so a reload still
    // polls. It never reaches the API: buildBlockCreatePayload whitelists the
    // fields it sends and `rewrite` is not one of them.
    setRewrite({ status: REWRITE_STATUS.PENDING, text: '', optimistic: true });

    try {
      await saveProject();
      const rewriteResponse = await rewriteProject(requestBody);
      if (!rewriteResponse?.status) {
        setRewrite(undefined);
        return;
      }
      // The job exists server-side now: same PENDING minus the marker, which is
      // what lets the poll start. UPDATE_BLOCK replaces `rewrite` wholesale
      // (redux/project/reducer.js:492), so the marker drops rather than merges.
      setRewrite({ status: REWRITE_STATUS.PENDING, text: '' });
      dispatch(actions.project.setAudioLinkSrt(null));
      dispatch(actions.project.setAudioSrtExpiredAt(null));
    } catch (error) {
      // Termination: a throw must not leave the block PENDING forever — nothing
      // else clears it, and the block would stay locked out of both operations.
      setRewrite(undefined);
    }
  };

  // The mirror of the rewrite guard, and it has to sit here rather than in
  // `isDisableConvertBtn`: NewSynthesisButton keeps its convert button live when
  // the block's audio is expired or its last synthesis failed
  // (`canConvertBlock`), and the single-block create-audio flow never passes
  // through `pickConvertibleBlocks`. Without this the two operations still
  // overlap on exactly those blocks.
  const handleConvertBlockToAudioGuarded = (blockToConvert) => {
    if (isBlockRewriting(block)) return;
    onConvertBlockToAudio(blockToConvert);
  };

  const handleCancelRewrite = () => {
    deleteBlockRewrite(projectId, blockId);
    // Only clears the `rewrite` draft (not content) — skip auto-save so
    // cancelling doesn't flip the save status to "Saving".
    dispatch(
      actions.project.updateBlock(
        blockId,
        {
          rewrite: undefined,
          blockStatus,
        },
        { skipAutoSave: true },
      ),
    );
  };

  const handleReplaceRewrite = () => {
    const field = hasTranslation ? 'translatedText' : 'text';
    const textRewrite = block?.rewrite?.text;
    const options = undoRedoEnabled
      ? {
          history: {
            commit: true,
            scope: HISTORY_SCOPE.DUBBING,
            label: HISTORY_LABEL.APPLY_REWRITE,
          },
        }
      : {};
    dispatch(
      actions.project.updateBlock(
        blockId,
        {
          [field]: textRewrite,
          blockStatus,
          rewrite: undefined,
        },
        options,
      ),
    );
    deleteBlockRewrite(projectId, blockId);
  };
  // V2 UI Improvement
  const blockRef = useRef(null);

  useEffect(() => {
    if (!isFocusing || !isBlockUIImprovement) return undefined;
    const handleClickOutside = (e) => {
      if (
        blockRef.current &&
        !blockRef.current.contains(e.target) &&
        !e.target.closest(
          '.MuiPopover-root, .MuiModal-root, .MuiDialog-root, .MuiPopper-root, .MuiPaper-root , .rc-tooltip, [data-no-blur-block]',
        )
      ) {
        onFocusingBlock(null);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFocusing, isBlockUIImprovement]);

  const getBlockBorderColor = ({ isError, isActive }) => {
    if (isError) return COLOR.functionalRed.default;
    if (isActive) return COLOR.indigo.default;
    return COLOR.label['16'];
  };

  const renderConfigBlock = () => (
    <ConfigOptionBlock
      handleOpenVoices={handleOpenVoices}
      handleChangeSpeed={handleChangeSpeed}
      handleChangeBreakTime={handleChangeBreakTime}
      handleChangeEmphasis={handleChangeEmphasis}
      voice={voice}
      speed={speed}
      breakTime={breakTime}
      hasEmphasis={hasEmphasis}
      emphasis={emphasis}
      isSynthesizing={isSynthesizing}
      onSplitBlock={handleSplitBlock}
      canSplitBlock={canSplitBlock}
      splitBlockDisableReason={
        splitBlockDisableReason ? t(splitBlockDisableReason) : null
      }
      isDubbingProject
      speakerConfigEnabled={speakerConfigEnabled}
      openVoice={!!popoverAnchorEl}
      speakerName={
        speakerConfigEnabled
          ? speakerName || t('defaultSpeakerName')
          : undefined
      }
    />
  );
  const renderCharacterCount = () => (
    <CreditsCount
      creditFactor={voice?.creditFactor}
      exceedBlockMaxCredits={exceedBlockMaxChar}
      credits={countTextLength(editableText)}
      maxCredits={MAX_CHARACTER_PER_BLOCK_SRT}
    />
  );

  const renderDrag = () => <div className="none-drag" />;

  const renderVoice = () => (
    <div className="voice">
      <Tooltip arrow title={t('chooseSpeaker')} placement="top">
        <IconButton
          disabled={isSynthesizing}
          style={{
            opacity: isSynthesizing ? 0.5 : 1,
            cursor: isSynthesizing ? 'not-allowed' : 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenVoices(e);
          }}
        >
          <BadgeAvatar
            width={25}
            smallImgWidth={10}
            img={voice?.roundImage}
            smallImg={voice?.language?.roundImage}
            type="image"
          />
        </IconButton>
      </Tooltip>
    </div>
  );
  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );
  const isSelected = useMemo(
    () => selectedBlockIds.includes(blockId),
    [selectedBlockIds, blockId],
  );
  const handleSelectBlock = () => {
    if (isSelected) {
      dispatch(actions.project.unselectBlocks([blockId]));
      return;
    }
    dispatch(actions.project.selectBlocks([blockId]));
  };

  const handleMergeBlock = () => {
    const mergedText = onMergeBlock(blockId);
    if (mergedText) {
      // Update editor state for current block
      const newContentState = ContentState.createFromText(mergedText);
      const newEditorState = EditorState.createWithContent(
        newContentState,
        generateDecorator(BREAK_TIME_REGEX, BreakTimeHighlight),
      );
      handleChangeContent(newEditorState);
    }
  };

  const renderSelectBlock = () => (
    <>
      <div className="select-block select-block-new-ui-v2">
        <Checkbox
          checked={isSelected}
          onChange={handleSelectBlock}
          disabled={isBlockSelectionLocked}
          className="checkbox-color"
        />
      </div>
    </>
  );

  const renderMergeBlockTooltip = () => {
    if (!areBlocksConsecutive)
      return (
        <NewTypography
          variant="body-14-medium"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('cannotMergeBecauseNotConsecutive'), {
              allowedTags: ['span', 'br'],
            }),
          }}
        />
      );

    if (isNextBlockSynthesizing || isSynthesizing)
      return (
        <NewTypography
          variant="body-14-medium"
          dangerouslySetInnerHTML={{
            __html: sanitize(t('cannotMergeBecauseExistBlockIsSynthesizing'), {
              allowedTags: ['span', 'br'],
            }),
          }}
        />
      );

    return null;
  };

  const renderMergeBlockButton = () => (
    <span style={{ display: 'inline-block' }}>
      <CommonButton
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleMergeBlock}
        variant="transparent"
        disabled={
          cannotMerge || isBlockRewriting(block) || isBlockRewriting(nextBlock)
        }
        startIcon={<MergeIcon sx={{ width: '20px', height: '20px' }} />}
        customStyle={{
          height: '24px',
          textTransform: 'none',
          borderRadius: '480px',
          border: `1px solid ${COLOR.indigo[32]}`,
          backgroundColor: COLOR.white,
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '-0.4px',
          textAlign: 'center',
          verticalAlign: 'middle',
          padding: '4px 16px',
          '&:hover': {
            backgroundColor: COLOR.whiteV2['64'],
            boxShadow: 'none',
          },
        }}
        textProps={{
          variantText: 'body-12-medium',
          color: COLOR.indigo,
        }}
      >
        {t('mergeBlock')}
      </CommonButton>
    </span>
  );

  const hasError =
    exceedBlockMaxChar ||
    hasInvalidCharacterLength ||
    hasInvalidCharacters ||
    hasBlackWords;

  const renderErrorWarning = () =>
    hasError && (
      <Tooltip
        arrow
        title={
          <Typography
            component="div"
            sx={{ fontSize: '14px', textAlign: 'left', display: 'block' }}
            dangerouslySetInnerHTML={{
              __html: (() => {
                const { message, sanitizeOptions } = getBlockErrorConfig();
                return sanitize(message, sanitizeOptions);
              })(),
            }}
          />
        }
        placement="left"
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className="exceed-block-char-icon"
        >
          <WarningRoundedIcon sx={{ color: COLOR.danger, fontSize: '24px' }} />
        </div>
      </Tooltip>
    );

  const renderRecommendedWarning = () =>
    !hasBlockError &&
    isExceededRecommendedChars &&
    !hasBlackWords && (
      <RecommendedCharWarning
        text={editableText}
        onOpenPopupNotEnoughCredits={onOpenPopupNotEnoughCredits}
        currentChars={currentChars}
        maxRecommended={maxRecommended}
        duration={blockDuration}
        rewriteEnabled={rewriteEnabled}
        voiceDefault={voiceDefault}
        onRewrite={handleRewrite}
        loadingRewriteProject={isBlockRewriting(block)}
        isSynthesizing={isSynthesizing}
      />
    );

  const renderMoreOption = () => (
    <MoreOptionBlock
      openMoreOptionBlock={openPopperBlockOption}
      onOpenDeleteDialog={handleOpenDeleteDialog}
      onOpenMoreOptionBlock={setOpenPopperBlockOption}
      onOpenReportDialog={handleOpenErrorReport}
      isDisabledDownloadBlockAudio={isDisablePlayAudio}
      onDownloadBlockAudio={handleDownloadBlockAudio}
      canDownloadBlockAudio={canDownloadBlockAudio}
      // A download is built from the blocks as they stand — deleting one while
      // it runs would ship a file that doesn't match the project. Same lock the
      // rest of the block's controls already sit behind (see isSynthesizing).
      isDisabledDeleteBlock={isDownloading}
      isRewriting={isBlockRewriting(block)}
      isDubbingProject
    />
  );

  const renderSpeakerInfo = () =>
    speakerConfigEnabled && (
      <>
        <span
          className={isBlockUIImprovement ? undefined : 'divider'}
          style={
            isBlockUIImprovement
              ? { color: COLOR.label2[64], margin: '0 4px' }
              : undefined
          }
        >
          |
        </span>
        <GraphicEqRoundedIcon
          sx={{ fontSize: '16px', color: COLOR.label2[64] }}
        />
        <NewTypography variant="body-14-regular" color={COLOR.label2[64]}>
          {speakerName || t('defaultSpeakerName')}
        </NewTypography>
        <LimitedFeature voiceFeatures={voice?.features} />
        {isVoiceCloningAboutToRemoved && (
          <Tooltip
            arrow
            title={t('voiceCloningDiscardAt', {
              date: convertFormatDate(voice?.discardAt),
            })}
            placement="top"
          >
            <RunningWithErrorsRoundedIcon
              sx={{
                color: COLOR.redPrimary,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        )}
        {isInValidPublicVoiceCloning && (
          <Tooltip arrow title={t('invalidPublicVoiceCloning')} placement="top">
            <WarningRoundedIcon
              sx={{
                color: COLOR.redPrimary,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        )}
      </>
    );

  return (
    <StyledContentBlockWrapper>
      <StyledContentBlock
        isLastParagraphId={isLastBlock}
        isFocusingOnBlock={
          isBlockUIImprovement
            ? isHighlight || isSelected
            : isFocusing || isSelected
        }
        isError={hasError}
        isHighlight={isHighlight}
      >
        {!isBlockUIImprovement && renderSelectBlock()}
        <StyledOrdinalNumberBlock>
          {/* Time Frame Row */}
          {editBlockTimeDubbingEnabled &&
            (isBlockUIImprovement ? (
              <StyledInfoBlock>
                <NewTypography
                  color={COLOR.descriptionUpload}
                  variant="body-14-regular"
                >
                  {indexBlock}.
                </NewTypography>
                <TimeFrameInput
                  value={block.startTime}
                  onChange={handleChangeStartTime}
                  onBlur={handleTimingBlur}
                  error={startTimeError}
                  disabled={isSynthesizing}
                />
                <span style={{ color: COLOR.label2[64] }}>-</span>
                <TimeFrameInput
                  value={block.endTime}
                  onChange={handleChangeEndTime}
                  onBlur={handleTimingBlur}
                  error={endTimeError}
                  disabled={isSynthesizing}
                />
              </StyledInfoBlock>
            ) : (
              <StyledTimeFrameRow>
                <span className="block-index">{indexBlock}.</span>
                <TimeFrameInput
                  value={block.startTime}
                  onChange={handleChangeStartTime}
                  onBlur={handleTimingBlur}
                  error={startTimeError}
                  disabled={isSynthesizing}
                />
                <span className="time-separator">-</span>
                <TimeFrameInput
                  value={block.endTime}
                  onChange={handleChangeEndTime}
                  onBlur={handleTimingBlur}
                  error={endTimeError}
                  disabled={isSynthesizing}
                />
                {renderSpeakerInfo()}
              </StyledTimeFrameRow>
            ))}

          <div
            className={classNames('block-wrapper', {
              'block-wrapper-v2': isBlockUIImprovement,
            })}
            ref={isBlockUIImprovement ? blockRef : undefined}
            role={isBlockUIImprovement ? 'presentation' : undefined}
            style={
              isBlockUIImprovement
                ? {
                    border: `0.5px solid ${getBlockBorderColor({
                      isError: hasError,
                      isActive: isHighlight || isSelected,
                    })}`,
                  }
                : undefined
            }
          >
            {isBlockUIImprovement && (
              <>
                {/* V2: Toolbar + Editor + CharCount */}
                <div
                  className="block-toolbar-wrapper"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      gap: '8px',
                    }}
                  >
                    {renderSelectBlock()}
                    <div className="block-header-divider" />
                    <ConfigOptionBlock
                      handleOpenVoices={handleOpenVoices}
                      handleChangeSpeed={handleChangeSpeed}
                      handleChangeBreakTime={handleChangeBreakTime}
                      handleChangeEmphasis={handleChangeEmphasis}
                      voice={voice}
                      speed={speed}
                      breakTime={breakTime}
                      hasEmphasis={hasEmphasis}
                      emphasis={emphasis}
                      isSynthesizing={isSynthesizing}
                      onSplitBlock={handleSplitBlock}
                      canSplitBlock={canSplitBlock}
                      splitBlockDisableReason={
                        splitBlockDisableReason
                          ? t(splitBlockDisableReason)
                          : null
                      }
                      isDubbingProject
                      speakerConfigEnabled={speakerConfigEnabled}
                      openVoice={!!popoverAnchorEl}
                      speakerName={
                        speakerConfigEnabled
                          ? speakerName || t('defaultSpeakerName')
                          : undefined
                      }
                      isBlockActive={isFocusing || isSelected}
                      isBlockUIImprovement
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {renderErrorWarning()}
                    {renderRecommendedWarning()}
                    <NewSynthesisButton
                      blockId={blockId}
                      synthesisText={editableText}
                      status={status}
                      isPlaying={isPlayingBlock}
                      isDisableConvertBtn={isDisableConvertBtn}
                      isConvertLocked={isBlockRewriting(block)}
                      onSaveProject={onSaveProject}
                      showSuccessIcon={showSuccessIcon}
                      audioExpiredAt={audioExpiredAt}
                      onOpenOutOfCreditsDialog={onOpenOutOfCreditsDialog}
                      onOpenWrongSyntaxDialog={onOpenWrongSyntaxDialog}
                      onOpenWrongSyntaxDialogV2={onOpenWrongSyntaxDialogV2}
                      checkValidCreateSynthesis={checkValidCreateSynthesis}
                      handlePlayAudio={handlePlayAudio}
                      onOpenDiscardedVoiceCloningDialog={
                        onOpenDiscardedVoiceCloningDialog
                      }
                      isDisablePlayAudio={isDisablePlayAudio}
                      onConvertBlockToAudio={handleConvertBlockToAudioGuarded}
                      enableDubbingBlockAudio={enableDubbingBlockAudio}
                      isBlockEditting={isBlockEditting}
                      enableCreditUsagePreview={enableCreditUsagePreview}
                      isBlockUIImprovement
                      isDubbingProject
                      audioLink={audioLink}
                      showAudioPopover={showAudioPopover}
                      popoverCurrentTime={popoverCurrentTime}
                      popoverDuration={popoverDuration}
                      onSeekInBlock={onSeekInBlock}
                    />
                    {renderMoreOption()}
                  </div>
                </div>
                <div className="editor-wrapper editor-wrapper-new-ui">
                  <StyledBlockEditor
                    onClick={handleFocusOnBlock}
                    tabIndex="-1"
                    isFocusingOnBlock={isFocusing}
                    isFocusLocked={isFocusLockedByPlayback}
                  >
                    <div className="editor-text-wrapper">
                      {hasTranslation && showOriginalText && (
                        <div className="original-text">{text}</div>
                      )}
                      <div
                        className={classNames('editor-container', {
                          'character-exceed': characterExceed,
                        })}
                      >
                        <Editor
                          placeholder={t('enterTextHere')}
                          editorState={content}
                          handlePastedText={handlePastedText}
                          handleKeyCommand={handleKeyCommand}
                          keyBindingFn={keyBindingFn}
                          stripPastedStyles
                          onChange={handleEditorChange}
                          onBlur={handleEditorBlur}
                          ref={inputRef}
                          readOnly={
                            isSynthesizing ||
                            isFocusLockedByPlayback ||
                            [
                              REWRITE_STATUS.PENDING,
                              REWRITE_STATUS.SUCCESS,
                            ].includes(block?.rewrite?.status)
                          }
                        />
                      </div>
                    </div>
                  </StyledBlockEditor>
                </div>
                {/* V2: Character count at bottom */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  {renderCharacterCount()}
                </div>
              </>
            )}
            {/* Original layout: hidden when V2 active, visible when V2 off */}
            {!isBlockUIImprovement && (
              <div className="block-toolbar-wrapper">
                {renderConfigBlock()}
                {renderCharacterCount()}
              </div>
            )}
            {/* Original: Editor + Sidebar toolbar */}
            <div
              style={isBlockUIImprovement ? { display: 'none' } : undefined}
              className="editor-wrapper editor-wrapper-new-ui"
            >
              <StyledBlockEditor
                onClick={handleFocusOnBlock}
                role="button"
                tabIndex="-1"
                isFocusingOnBlock={isFocusing}
                isFocusLocked={isFocusLockedByPlayback}
              >
                {renderVoice()}
                <div className="editor-text-wrapper">
                  {hasTranslation && showOriginalText && (
                    <div className="original-text">{text}</div>
                  )}
                  <div
                    className={classNames('editor-container', {
                      'character-exceed': characterExceed,
                    })}
                  >
                    {!isBlockUIImprovement && (
                      <Editor
                        placeholder={t('enterTextHere')}
                        editorState={content}
                        handlePastedText={handlePastedText}
                        handleKeyCommand={handleKeyCommand}
                        keyBindingFn={keyBindingFn}
                        stripPastedStyles
                        onChange={handleEditorChange}
                        onBlur={handleEditorBlur}
                        ref={inputRef}
                        readOnly={isSynthesizing || isFocusLockedByPlayback}
                      />
                    )}
                  </div>
                </div>
              </StyledBlockEditor>
              <div className="editor-toolbar editor-toolbar-new-ui">
                {renderErrorWarning()}
                {renderRecommendedWarning()}
                <SynthesisButton
                  blockId={blockId}
                  synthesisText={editableText}
                  status={status}
                  isPlaying={isPlayingBlock}
                  isDisableConvertBtn={isDisableConvertBtn}
                  onSaveProject={onSaveProject}
                  showSuccessIcon={showSuccessIcon}
                  audioExpiredAt={audioExpiredAt}
                  onOpenOutOfCreditsDialog={onOpenOutOfCreditsDialog}
                  onOpenWrongSyntaxDialog={onOpenWrongSyntaxDialog}
                  onOpenWrongSyntaxDialogV2={onOpenWrongSyntaxDialogV2}
                  checkValidCreateSynthesis={checkValidCreateSynthesis}
                  handlePlayAudio={handlePlayAudio}
                  onOpenDiscardedVoiceCloningDialog={
                    onOpenDiscardedVoiceCloningDialog
                  }
                  isDisablePlayAudio={isDisablePlayAudio}
                  onConvertBlockToAudio={handleConvertBlockToAudioGuarded}
                  enableDubbingBlockAudio={enableDubbingBlockAudio}
                  isBlockEditting={isBlockEditting}
                  isDubbingProject
                />
                {renderMoreOption()}
              </div>
            </div>
            {rewriteEnabled && block?.rewrite && (
              <Rewrite
                rewriteBlock={block?.rewrite?.text}
                isRewriting={isBlockRewriting(block)}
                isRewriteFailure={
                  block?.rewrite?.status === REWRITE_STATUS.FAILURE
                }
                onCancelRewrite={handleCancelRewrite}
                onReplaceRewrite={handleReplaceRewrite}
                onRewrite={handleRewrite}
                textBlock={editableText}
                onOpenPopupNotEnoughCredits={onOpenPopupNotEnoughCredits}
                isSynthesizing={isSynthesizing}
                isProjectSynthesizing={isProjectSynthesizing}
              />
            )}
          </div>
        </StyledOrdinalNumberBlock>
        {!isBlockUIImprovement && renderDrag()}
        {speakerConfigEnabled ? (
          <>
            <SpeakerSelectionPopover
              open={Boolean(popoverAnchorEl)}
              anchorEl={popoverAnchorEl}
              onClose={handleClosePopover}
              speakers={speakers}
              currentSpeaker={speaker}
              voiceDefault={voiceDefault}
              onAddSpeaker={handleOpenAddSpeaker}
              onSelectSpeaker={handleChangeSpeaker}
              user={user}
            />
            <AddSpeaker
              open={openAddSpeaker}
              onClose={handleCloseAddSpeaker}
              isApplyPreset
              projectId={projectId}
              selectedBlockIds={[blockId]}
            />
          </>
        ) : (
          <>
            {openVoices && (
              <VoiceSelectionDialog
                open={openVoices}
                activeVoiceId={voice && voice.id}
                activeVoiceCode={voice && voice.code}
                voiceStatus={voice && voice.status}
                onClose={handleCloseVoices}
                onChangeVoice={handleChangeVoice}
                hasLanguageFilter
              />
            )}
          </>
        )}
      </StyledContentBlock>
      {!isLastBlock && (
        <StyledAddNewBlockZone isDubbingProject>
          <div className="zone-wrapper">
            {!isBlockUIImprovement && <div className="zone-wrapper-left" />}
            <Typography
              variant="body2"
              color="text.secondary"
              className="command-to-add-new-block"
            >
              {t('commandToAddNewBlock')}
            </Typography>
            <hr className="border" />
            <>
              {cannotMerge ? (
                <Tooltip
                  arrow
                  title={renderMergeBlockTooltip()}
                  placement="top"
                  maxWidth="550px"
                >
                  {renderMergeBlockButton()}
                </Tooltip>
              ) : (
                renderMergeBlockButton()
              )}
            </>
            {!canInsertBlockBetween ? (
              <Tooltip
                arrow
                title={
                  <NewTypography
                    variant="body-14-medium"
                    dangerouslySetInnerHTML={{
                      __html: sanitize(
                        t('cannotAddBlockDueToInsufficientGap'),
                        {
                          allowedTags: ['span', 'br'],
                        },
                      ),
                    }}
                  />
                }
                placement="top"
              >
                <span>
                  <CommonButton
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleMergeBlock}
                    variant="transparent"
                    disabled={!canInsertBlockBetween}
                    startIcon={
                      <img className="add-btn-icon" src={addBlock} alt="icon" />
                    }
                    customStyle={{
                      height: '24px',
                      textTransform: 'none',
                      borderRadius: '480px',
                      border: `1px solid ${COLOR.indigo[32]}`,
                      backgroundColor: COLOR.white,
                      fontWeight: 500,
                      fontSize: '12px',
                      lineHeight: '16px',
                      letterSpacing: '-0.4px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      padding: '4px 16px',
                      '&:hover': {
                        backgroundColor: COLOR.whiteV2['64'],
                        boxShadow: 'none',
                      },
                    }}
                    textProps={{
                      variantText: 'body-12-medium',
                      color: COLOR.indigo,
                    }}
                  >
                    {t('addMoreBlock')}
                  </CommonButton>
                </span>
              </Tooltip>
            ) : (
              <CommonButton
                onClick={handleAddNewParagraphs}
                variant="transparent"
                disabled={isSynthesizing}
                startIcon={
                  <img className="add-btn-icon" src={addBlock} alt="icon" />
                }
                customStyle={{
                  height: '24px',
                  textTransform: 'none',
                  borderRadius: '480px',
                  border: `1px solid ${COLOR.indigo[32]}`,
                  backgroundColor: COLOR.white,
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  letterSpacing: '-0.4px',
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  padding: '4px 16px',
                  '&:hover': {
                    backgroundColor: COLOR.whiteV2['64'],
                    boxShadow: 'none',
                  },
                }}
                textProps={{
                  variantText: 'body-12-medium',
                  color: COLOR.indigo,
                }}
              >
                {t('addMoreBlock')}
              </CommonButton>
            )}
            <div className="none-drag" />
          </div>
        </StyledAddNewBlockZone>
      )}
    </StyledContentBlockWrapper>
  );
};

function arePropsEqual(prevProps, nextProps) {
  // Check translatedText separately to detect translation changes
  const blockEqual =
    prevProps.block === nextProps.block ||
    (prevProps.block?.translatedText === nextProps.block?.translatedText &&
      prevProps.block?.text === nextProps.block?.text &&
      prevProps.block?.voiceCode === nextProps.block?.voiceCode &&
      prevProps.block?.speed === nextProps.block?.speed &&
      prevProps.block?.audioLink === nextProps.block?.audioLink &&
      prevProps.block?.status === nextProps.block?.status &&
      prevProps.block?.startTime === nextProps.block?.startTime &&
      prevProps.block?.endTime === nextProps.block?.endTime &&
      prevProps.block?.rewrite?.status === nextProps.block?.rewrite?.status &&
      prevProps.block?.rewrite?.text === nextProps.block?.rewrite?.text &&
      prevProps.onOpenPopupNotEnoughCredits ===
        nextProps.onOpenPopupNotEnoughCredits &&
      prevProps.saveProject === nextProps.saveProject);

  // The audio popover only reads the playback clock while THIS block is the one
  // playing (see `isThisBlockActive`), so compare the clock for that block only —
  // every other block keeps bailing out on each `timeupdate`.
  //
  // These three props were never compared here, which used to be harmless: the
  // handlers above (`saveProject`, `onOpenPopupNotEnoughCredits`) were rebuilt on
  // every parent render, so a `timeupdate` always failed this comparison and the
  // block re-rendered with a fresh clock by accident. Once those handlers were
  // given stable identities, nothing here changed during playback any more and
  // the popover time froze.
  const prevPlaying = prevProps.currentPlayingBlockId === prevProps.blockId;
  const nextPlaying = nextProps.currentPlayingBlockId === nextProps.blockId;
  const audioPreviewEqual =
    prevPlaying === nextPlaying &&
    (!nextPlaying ||
      (prevProps.blockAudioCurrentTime === nextProps.blockAudioCurrentTime &&
        prevProps.blockAudioDuration === nextProps.blockAudioDuration));

  return (
    audioPreviewEqual &&
    prevProps.block?.translatedText === nextProps.block?.translatedText &&
    prevProps.block?.text === nextProps.block?.text &&
    prevProps.blockId === nextProps.blockId &&
    prevProps.characterExceed === nextProps.characterExceed &&
    prevProps.isLastBlock === nextProps.isLastBlock &&
    prevProps.isFocusing === nextProps.isFocusing &&
    blockEqual &&
    prevProps.disabledMoveUp === nextProps.disabledMoveUp &&
    prevProps.voiceDefault === nextProps.voiceDefault &&
    prevProps.listVoice === nextProps.listVoice &&
    prevProps.isPlaying === nextProps.isPlaying &&
    prevProps.hasVideoTimeline === nextProps.hasVideoTimeline &&
    prevProps.isDownloading === nextProps.isDownloading &&
    prevProps.isHighlight === nextProps.isHighlight &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.isPlayingBlock === nextProps.isPlayingBlock &&
    prevProps.originalBlock === nextProps.originalBlock &&
    prevProps.audioType === nextProps.audioType &&
    prevProps.nextBlock === nextProps.nextBlock &&
    prevProps.prevBlock === nextProps.prevBlock &&
    prevProps.speakers === nextProps.speakers &&
    prevProps.areBlocksConsecutive === nextProps.areBlocksConsecutive &&
    prevProps.foundBlackWords?.join(',') ===
      nextProps.foundBlackWords?.join(',') &&
    prevProps.isBlockUIImprovement === nextProps.isBlockUIImprovement &&
    prevProps.isBlockSelectionLocked === nextProps.isBlockSelectionLocked &&
    prevProps.block?.rewrite?.status === nextProps.block?.rewrite?.status &&
    prevProps.block?.rewrite?.text === nextProps.block?.rewrite?.text &&
    prevProps.onOpenPopupNotEnoughCredits ===
      nextProps.onOpenPopupNotEnoughCredits &&
    prevProps.saveProject === nextProps.saveProject
  );
}

export default memo(ContentBlock, arePropsEqual);
